import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { errorMessage, toastMessage } from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { License, NewOrderInitiationProps, State } from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import TextField from '@mui/material/TextField';
import { useUnsavedChangesPrompt } from '@hooks/useNavigationBlocker';

/**
 * AddOrEditLicense Component
 *
 * This component is a modal used for adding or editing a license.
 * It allows users to modify the license details before saving them.
 *
 * @param onClose - Callback function to close the modal.
 * @param licenseToEdit - The license to be edited.
 */
export const AddOrEditLicense: React.FC<{
  onClose: () => void;
  licenseToEdit?: License | null;
}> = ({ onClose, licenseToEdit }) => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const [customerId, setCustomerId] = useState<number>(0);
  const [states, setStates] = useState<State[]>([]);
  const { showConfirmationToast } = UseConfirmationToast();
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();
  // License state
  const [licenses, setLicenses] = useState<License[]>([
    {
      id: licenseToEdit?.id ?? -1,
      harvestStateId: licenseToEdit?.harvestStateId ?? 0,
      firstName: licenseToEdit?.firstName || customerDetails?.firstName || '',
      lastName: licenseToEdit?.lastName || customerDetails?.lastName || '',
      licenseNumber: licenseToEdit?.licenseNumber || '',
      expiryDate: licenseToEdit?.expiryDate || '',
    },
  ]);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  useUnsavedChangesPrompt(hasUnsavedChanges);

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    harvestStateId: '',
    licenseNumber: '',
    expiryDate: '',
  });

  /**
   * Effect hook that initializes the license state with the provided license data.
   *
   * @remarks
   * This effect runs once when the `licenseToEdit` prop changes.
   * It updates the `licenses` state with the provided license data, formatting the expiry date to MM/DD/YYYY.
   *
   * @param licenseToEdit - The license object to be edited.
   */
  useEffect(() => {
    if (licenseToEdit) {
      setLicenses([
        {
          id: licenseToEdit.id,
          harvestStateId: licenseToEdit.harvestStateId,
          firstName: licenseToEdit.firstName,
          lastName: licenseToEdit.lastName,
          licenseNumber: licenseToEdit.licenseNumber,
          expiryDate: dayjs(licenseToEdit.expiryDate).format('MM/DD/YYYY'),
        },
      ]);
    }
  }, [licenseToEdit]);

  /**
   * Effect hook that initializes the customer context and fetches available butcher species.
   *
   * @remarks
   * This effect runs once on component mount. It attempts to retrieve customer details from
   * local storage. If customer details are found, it sets the customer ID in state.
   * If not, it redirects the user to the customer list page.
   * Regardless of whether customer details are found, it triggers a fetch of butcher species data.
   */
  useEffect(() => {
    const customerID = CustomerOrderService.getCustomerId();
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);

    if (customerID) {
      setCustomerId(customerID);
      fetchStates();
      if (!licenseToEdit) {
        setLicenses([
          {
            id: -1,
            harvestStateId: 0,
            firstName: customerDetails?.firstName || '',
            lastName: customerDetails?.lastName || '',
            licenseNumber: '',
            expiryDate: '',
          },
        ]);
      }
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  }, []);

  /**
   * Fetches the list of butcher species from the server and initializes their quantities.
   *
   * @remarks
   * This asynchronous function sends a POST request to fetch master data of type `1` from the backend.
   * Once the species data is received, it initializes the `quantity` property of each species to `0`
   * and updates the component state via `setSpecies`.
   *
   * If an error occurs during the fetch, an error toast is displayed to the user.
   */
  const fetchStates = async () => {
    try {
      const response = await Axios.get(ApiEndpoints.FETCH_STATES);
      setStates(response.data.data);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Updates a specific field of a license at the given index in the licenses state.
   *
   * @function handleLicenseChange
   * @param {number} index - The index of the license in the licenses array to update.
   * @param {keyof License} field - The license field (property name) to update.
   * @param {string | number} value - The new value to assign to the specified field.
   *
   * @returns {void} Does not return anything; updates the licenses state.
   *
   * @description
   * - Creates a shallow copy of the licenses state.
   * - Updates the given field of the license at the specified index.
   * - Returns the new licenses array, ensuring immutability of state.
   *
   */
  const handleLicenseChange = (
    index: number,
    field: keyof License,
    value: string | number,
  ): void => {
    setHasUnsavedChanges(true);
    setLicenses((prev) => {
      const updatedLicenses = [...prev];
      updatedLicenses[index] = {
        ...updatedLicenses[index],
        [field]: value,
      };
      return updatedLicenses;
    });
  };

  /**
   * Handles the cancellation of license editing by showing a confirmation toast.
   *
   * @function cancelLicense
   * @returns {void} Does not return anything; triggers a confirmation toast.
   *
   * @description
   * - Displays a confirmation toast with a discard message.
   * - Provides an action button labeled "Discard".
   * - Calls `onClose` when the discard action is confirmed.
   *
   */
  const cancelLicense = (): void => {
    showConfirmationToast(
      toastMessage.discardConfirmation,
      'Discard Changes',
      'delete',
      onClose,
      'Discard', // Action button name
      () => {},
      'Cancel', // Cancel button name
    );
  };

  /**
   * Validates the license form data.
   *
   * @returns {boolean} True if the form is valid, false otherwise.
   *
   * @description
   * - Checks if the first name is empty.
   * - Checks if the last name is empty.
   * - Checks if the harvest state ID is zero (indicating no state selected).
   * - Checks if the license number is empty.
   * - Checks if the expiry date is empty.
   * - Updates the `errors` state with any validation messages.
   * - Returns `true` if all fields are valid, `false` otherwise.
   */
  const validateForm = (): boolean => {
    const newErrors = {
      firstName: !licenses[0].firstName?.trim() ? 'First name is required' : '',
      lastName: !licenses[0].lastName?.trim() ? 'Last name is required' : '',
      harvestStateId: !licenses[0].harvestStateId ? 'State is required' : '',
      licenseNumber: !licenses[0].licenseNumber?.trim()
        ? 'License number is required'
        : '',
      expiryDate: !licenses[0].expiryDate?.trim()
        ? 'Expiry date is required'
        : '',
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((e) => e === '');
  };

  /**
   * Handles saving or updating a customer's license details.
   *
   * @async
   * @function handleSaveLicenseDetails
   * @param {React.FormEvent} e - The form submission event to prevent default behavior.
   * @param {number} index - The index of the license in the licenses array to save.
   *
   * @returns {Promise<void>} Resolves once the license is validated, saved, and state is updated.
   *
   * @description
   * - Prevents default form submission behavior.
   * - Retrieves the license item at the given index from state.
   * - Validates that all required license fields are filled in.
   *   - If validation fails, shows an error toast and stops execution.
   * - Builds a `payload` object for the API request, formatting dates and applying fallbacks from `customerDetails`.
   * - Calls the `CUSTOMER_LICENSE_UPSERT` API endpoint to create or update the license.
   * - Refreshes license details from the server after saving.
   * - Shows a success toast if the operation completes successfully.
   * - Updates the license in local state with the response data (if provided).
   * - Handles errors gracefully by showing an error toast.
   *
   */
  const handleSaveLicenseDetails = async (
    e: React.FormEvent,
    index: number,
  ) => {
    e.preventDefault();
    if (!validateForm()) return;
    setHasUnsavedChanges(false);
    try {
      const licenseItem = licenses[index];
      if (
        !licenseItem.firstName?.trim() ||
        !licenseItem.lastName?.trim() ||
        !licenseItem.harvestStateId ||
        !licenseItem.licenseNumber?.trim() ||
        !licenseItem.expiryDate?.trim()
      ) {
        showToastMessage(errorMessage.LICENSE_VALIDATION_FAILURE, 'error');
        return;
      }

      const payload = {
        id: licenseItem?.id ?? -1,
        harvestStateId: licenseItem?.harvestStateId ?? 0,
        firstName: licenseItem?.firstName || customerDetails?.firstName || '',
        lastName: licenseItem?.lastName || customerDetails?.lastName || '',
        licenseNumber: licenseItem?.licenseNumber || '',
        expiryDate: licenseItem?.expiryDate
          ? dayjs(licenseItem.expiryDate).format('MM/DD/YYYY')
          : '',
      };

      const response = await Axios.post(ApiEndpoints.CUSTOMER_LICENSE_UPSERT, {
        customerId: customerId,
        license: payload,
      });

      if (response.data?.data?.license) {
        const savedLicense = response.data.data.license;

        const harvestState = states.find(
          (s) => s.id === savedLicense.harvestStateId,
        );
        const finalLicense = {
          ...savedLicense,
          HarvestState: harvestState
            ? { id: harvestState.id, stateName: harvestState.stateName }
            : null,
        };

        const updated = [...licenses];
        updated[index] = finalLicense;
        setLicenses(updated);

        const existingCustomerDetails =
          CustomerOrderService.getCustomerDetails();
        const updatedCustomerDetails: NewOrderInitiationProps = {
          ...existingCustomerDetails,
          HuntingLicences: [
            ...(existingCustomerDetails?.HuntingLicences || []).filter(
              (l) => l.id !== finalLicense.id,
            ),
            finalLicense,
          ],
        } as NewOrderInitiationProps;
        CustomerOrderService.setCustomerDetails(updatedCustomerDetails);
      }
      onClose();
    } catch (error) {
      showToastMessage(errorMessage.ERROR_UPDATING_CUSTOMER, 'error');
    }
  };

  /**
   * Handles closing the modal.
   *
   * If there are no unsaved changes, it will immediately close the modal
   * by calling `onClose`. If unsaved changes exist, it will instead call
   * `cancelLicense` to show a warning or perform cleanup.
   *
   * @function handleClose
   *
   * @returns {void}
   *
   */
  const handleClose = () => {
    // If no unsaved changes, dismiss the modal otherwise, show the warning toast
    if (!hasUnsavedChanges) {
      onClose();
    } else {
      cancelLicense();
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-lg shadow-lg p-3">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-1">
          <h2 className="text-xl font-medium">
            {licenseToEdit ? 'Edit license' : 'Add New license'}{' '}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-600 hover:text-gray-900"
          >
            ✕
          </button>
        </div>
        <span className="p-1 text-sm text-[#000A1666]">
          (Please enter the details below as per the license)
        </span>
        {/* Add new license form */}
        <div className="p-1">
          <div className="bg-[#FFFEFA] mb-3 mt-3 shadown-lg rounded-lg mx-3 p-3 border border-gray-300">
            <div className="flex w-full justify-start items-start">
              <div className="w-1/3">
                <label htmlFor="firstName" className="block mb-2">
                  First Name <sup>*</sup>
                </label>
                <input
                  name="firstName"
                  type="text"
                  value={licenses[0].firstName}
                  onChange={(e) => {
                    handleLicenseChange(0, 'firstName', e.target.value);
                    setErrors((prev) => ({ ...prev, firstName: '' }));
                  }}
                  placeholder="First Name"
                  className={`p-2 border rounded-md text-gray-600 w-11/12 lg:w-9/12 focus:outline-primary ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                />
                <p className="text-red-500 text-xs mt-1 min-h-[16px]">
                  {errors.firstName}
                </p>
              </div>

              <div className="w-1/3">
                <label htmlFor="lastName" className="block mb-2">
                  Last Name <sup>*</sup>
                </label>
                <input
                  name="lastName"
                  type="text"
                  value={licenses[0].lastName}
                  onChange={(e) => {
                    handleLicenseChange(0, 'lastName', e.target.value);
                    setErrors((prev) => ({ ...prev, lastName: '' }));
                  }}
                  placeholder="Last Name"
                  className={`p-2 border rounded-md text-gray-600 w-11/12 lg:w-9/12 focus:outline-primary ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                />
                <p className="text-red-500 text-xs mt-1 min-h-[16px]">
                  {errors.lastName}
                </p>
              </div>

              <div className="w-1/3">
                <label htmlFor="state" className="block mb-2">
                  State <sup>*</sup>
                </label>
                <select
                  name="state"
                  value={licenses[0].harvestStateId}
                  onChange={(e) => {
                    handleLicenseChange(
                      0,
                      'harvestStateId',
                      parseInt(e.target.value),
                    );
                    setErrors((prev) => ({ ...prev, harvestStateId: '' }));
                  }}
                  className={`p-2 border rounded-md text-gray-600 w-11/12 lg:w-9/12 focus:outline-primary ${errors.harvestStateId ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Select state</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.stateName}
                    </option>
                  ))}
                </select>
                <p className="text-red-500 text-xs mt-1 min-h-[16px]">
                  {errors.harvestStateId}
                </p>
              </div>
            </div>
            <div className="w-full flex justify-start items-start mb-4">
              <div className="w-1/3">
                <label htmlFor="licenseNumber" className="block mb-2">
                  License Number <sup>*</sup>
                </label>
                <input
                  name="licenseNumber"
                  type="text"
                  value={licenses[0].licenseNumber}
                  onChange={(e) => {
                    handleLicenseChange(0, 'licenseNumber', e.target.value);
                    setErrors((prev) => ({ ...prev, licenseNumber: '' }));
                  }}
                  placeholder="License Number"
                  className={`p-2 border rounded-md text-gray-600 w-11/12 lg:w-9/12 focus:outline-primary ${errors.licenseNumber ? 'border-red-500' : 'border-gray-300'}`}
                />
                <p className="text-red-500 text-xs mt-1 min-h-[16px]">
                  {errors.licenseNumber}
                </p>
              </div>

              <div className="w-1/3 relative">
                <label htmlFor="expiryDate" className="block mb-2">
                  License Expiry <sup>*</sup>
                </label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    disablePast
                    format="MM/DD/YYYY"
                    value={
                      licenses[0].expiryDate
                        ? dayjs(licenses[0].expiryDate)
                        : null
                    }
                    onChange={(newValue) => {
                      if (newValue) {
                        handleLicenseChange(
                          0,
                          'expiryDate',
                          newValue.format('MM/DD/YYYY'),
                        );
                        setErrors((prev) => ({ ...prev, expiryDate: '' }));
                      }
                    }}
                    desktopModeMediaQuery="@media (min-width: 0px)"
                    slots={{ textField: TextField }}
                    slotProps={{
                      textField: {
                        placeholder: 'MM/DD/YYYY',
                        size: 'small',
                        error: !!errors.expiryDate,
                        sx: {
                          textDecorationColor: '#4b5563',
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        },
                        InputProps: {
                          sx: {
                            border: `1px solid ${errors.expiryDate ? '#ef4444' : '#d1d5db'}`,
                            borderRadius: '0.375rem',
                            '&.Mui-focused': { borderColor: '#240089' },
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <p className="text-red-500 text-xs mt-1 min-h-[16px]">
                  {errors.expiryDate}
                </p>
              </div>
            </div>

            <div className="flex align-middle justify-end items-center">
              <div>
                <button
                  onClick={cancelLicense}
                  className="text-primary font-bold"
                  type="button"
                >
                  Cancel
                </button>
              </div>
              <div>
                <button
                  onClick={(event) => handleSaveLicenseDetails(event, 0)}
                  className="bg-primary w-24 text-white px-4 ml-8 py-2 my-8 rounded-md"
                  type="button"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
